/* Copyright (c) 2024 Xperi. All Rights Reserved. */

/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
/* eslint-disable no-bitwise */
/**
 * This is a purely static utility class for Parental Controls Reset PIN generation.
 * This class is direct conversion of ParentalControlsUtil class present in clientcore
 * path: https://opengrok.tivo.com/source/xref/clientcore/mainline/shared/rpchandlers/rpchandlers/lib/com/tivo/shared/rpchandlers/parentalcontrols/ParentalControlsUtil.hx
 * any changes in clientcore class algorithm needs to be updated here also.
 */
export class ParentalControlsUtil {
    private static readonly MAX_VALUE: bigint = 4294967296n;

    private static readonly WEEK_DAYS_LONG: string[] = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ];

    private static readonly MONTHS_LONG: string[] = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    private static add(value1: bigint, value2: bigint): bigint {
        let retVal = value1 + value2;
        if (retVal >= ParentalControlsUtil.MAX_VALUE) {
            retVal -= ParentalControlsUtil.MAX_VALUE;
        }
        return retVal;
    }

    private static subtract(value1: bigint, value2: bigint): bigint {
        let retVal = value1 - value2;
        if (retVal < 0) {
            retVal += ParentalControlsUtil.MAX_VALUE;
        }
        return retVal;
    }

    private static leftShift(value1: bigint, value2: number): bigint {
        let retVal = value1;
        for (let i = 0; i < value2; ++i) {
            retVal *= 2n;
            if (retVal >= ParentalControlsUtil.MAX_VALUE) {
                retVal %= ParentalControlsUtil.MAX_VALUE;
            }
        }
        return retVal;
    }

    private static mix(a: bigint, b: bigint, c: bigint): { aI: bigint; bI: bigint; cI: bigint } {
        a = ParentalControlsUtil.subtract(a, b);
        a = ParentalControlsUtil.subtract(a, c);
        a ^= c >> 13n;
        b = ParentalControlsUtil.subtract(b, c);
        b = ParentalControlsUtil.subtract(b, a);
        b ^= ParentalControlsUtil.leftShift(a, 8);
        c = ParentalControlsUtil.subtract(c, a);
        c = ParentalControlsUtil.subtract(c, b);
        c ^= b >> 13n;
        a = ParentalControlsUtil.subtract(a, b);
        a = ParentalControlsUtil.subtract(a, c);
        a ^= c >> 12n;
        b = ParentalControlsUtil.subtract(b, c);
        b = ParentalControlsUtil.subtract(b, a);
        b ^= ParentalControlsUtil.leftShift(a, 16);
        c = ParentalControlsUtil.subtract(c, a);
        c = ParentalControlsUtil.subtract(c, b);
        c ^= b >> 5n;
        a = ParentalControlsUtil.subtract(a, b);
        a = ParentalControlsUtil.subtract(a, c);
        a ^= c >> 3n;
        b = ParentalControlsUtil.subtract(b, c);
        b = ParentalControlsUtil.subtract(b, a);
        b ^= ParentalControlsUtil.leftShift(a, 10);
        c = ParentalControlsUtil.subtract(c, a);
        c = ParentalControlsUtil.subtract(c, b);
        c ^= b >> 15n;

        return { aI: a, bI: b, cI: c };
    }

    private static getHashByte(hashString: string): bigint {
        // the golden ratio; an arbitrary value
        let a = 2654435769n;
        let b = 2654435769n;
        let c = 0n; // the previous hash value (seed)

        let len = hashString.length;
        let index = 0;

        while (len >= 12) {
            // Handle most of the key
            a = ParentalControlsUtil.add(
                a,
                ParentalControlsUtil.add(
                    BigInt(hashString.charCodeAt(index)),
                    ParentalControlsUtil.add(
                        ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 1)), 8),
                        ParentalControlsUtil.add(
                            ParentalControlsUtil.leftShift(
                                BigInt(hashString.charCodeAt(index + 2)),
                                16,
                            ),
                            ParentalControlsUtil.leftShift(
                                BigInt(hashString.charCodeAt(index + 3)),
                                24,
                            ),
                        ),
                    ),
                ),
            );
            b = ParentalControlsUtil.add(
                b,
                ParentalControlsUtil.add(
                    BigInt(hashString.charCodeAt(index + 4)),
                    ParentalControlsUtil.add(
                        ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 5)), 8),
                        ParentalControlsUtil.add(
                            ParentalControlsUtil.leftShift(
                                BigInt(hashString.charCodeAt(index + 6)),
                                16,
                            ),
                            ParentalControlsUtil.leftShift(
                                BigInt(hashString.charCodeAt(index + 7)),
                                24,
                            ),
                        ),
                    ),
                ),
            );
            c = ParentalControlsUtil.add(
                c,
                ParentalControlsUtil.add(
                    BigInt(hashString.charCodeAt(index + 8)),
                    ParentalControlsUtil.add(
                        ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 9)), 8),
                        ParentalControlsUtil.add(
                            ParentalControlsUtil.leftShift(
                                BigInt(hashString.charCodeAt(index + 10)),
                                16,
                            ),
                            ParentalControlsUtil.leftShift(
                                BigInt(hashString.charCodeAt(index + 11)),
                                24,
                            ),
                        ),
                    ),
                ),
            );

            const mixVal = ParentalControlsUtil.mix(a, b, c);
            a = mixVal.aI;
            b = mixVal.bI;
            c = mixVal.cI;

            index += 12;
            len -= 12;
        }

        // Handle the last 11 bytes
        c = ParentalControlsUtil.add(c, BigInt(hashString.length));

        // the case fall through last 11 bytes
        if (len === 11) {
            c = ParentalControlsUtil.add(
                c,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 10)), 24),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 10) {
            c = ParentalControlsUtil.add(
                c,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 9)), 16),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 9) {
            c = ParentalControlsUtil.add(
                c,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 8)), 8),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 8) {
            b = ParentalControlsUtil.add(
                b,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 7)), 24),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 7) {
            b = ParentalControlsUtil.add(
                b,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 6)), 16),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 6) {
            b = ParentalControlsUtil.add(
                b,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 5)), 8),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 5) {
            b = ParentalControlsUtil.add(b, BigInt(hashString.charCodeAt(index + 4)));
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 4) {
            a = ParentalControlsUtil.add(
                a,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 3)), 24),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 3) {
            a = ParentalControlsUtil.add(
                a,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 2)), 16),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 2) {
            a = ParentalControlsUtil.add(
                a,
                ParentalControlsUtil.leftShift(BigInt(hashString.charCodeAt(index + 1)), 8),
            );
            --len;
        }

        // the case fall through last 11 bytes
        if (len === 1) {
            a = ParentalControlsUtil.add(a, BigInt(hashString.charCodeAt(index)));
            --len;
        }

        const mixVal = ParentalControlsUtil.mix(a, b, c);
        c = mixVal.cI;
        c %= 10000n;

        if (c < 1000n) {
            c += 1000n;
        }

        return c;
    }

    /**
     * Returns an encrypted Parental Controls Password for user so that
     * when ever user forgets they can use this password to get unlocked.
     * This produces password the same as what is done by harmony C++ code and
     * service code to generate a parental controls PIN reset password.
     * @param bodyId
     */
    public static getHashedPassword(bodyId: string): string {
        // Remove the 'tsn:' prefix
        const serialNumber = bodyId?.substring(4) || '-';

        // Get current UTC time
        const now = new Date();
        const utcDate = {
            day: now.getUTCDate(),
            year: now.getUTCFullYear(),
            weekday: now.getUTCDay(),
            month: now.getUTCMonth(),
        };

        // Construct the date string
        const strDayYear = ` ${utcDate.day}, ${utcDate.year}`;
        const strDate = `${ParentalControlsUtil.WEEK_DAYS_LONG[utcDate.weekday]}, ${
            ParentalControlsUtil.MONTHS_LONG[utcDate.month]
        }${strDayYear}`;

        // Concatenate serialNumber and strDate
        const strSerialAndDate = serialNumber + strDate;

        // Get hashed password
        const hashedPassword = ParentalControlsUtil.getHashByte(strSerialAndDate);

        // Convert to string and return
        return hashedPassword.toString();
    }
}
